import React, { useState } from 'react'
import { AdCreative, AIAspectRatio, AIGenerationMode } from '@marketing-milk/interfaces'
import { GenerationControls } from './GenerationControls'
import { PromptEditor } from './PromptEditor'
import { PickerSelectedImages } from './PickerSelectedImages'
import { CreativeMedia } from 'pages/business/creatives/CreativeCard/CreativeMedia'
import { PickerFile } from '../useGooglePicker'

const IMAGE_LIMIT = 500
const TEXT_LIMIT = 300

const IMAGE_PRESETS = [
  {
    id: 'keep_template_change_bg_ref',
    label: 'Keep template, change background using reference photos',
    prompt:
      'Keep the existing text layout, headlines, and copy placement exactly as-is. Replace only the background using the provided reference photos as visual inspiration.',
  },
  {
    id: 'keep_template_new_bg',
    label: 'Keep template, generate new background image',
    prompt:
      'Keep the existing text layout, headlines, and copy placement exactly as-is. Generate a new, fresh background scene that complements the ad.',
  },
  {
    id: 'new_template_keep_bg',
    label: 'New template, keep background image',
    prompt:
      'Keep the existing background image intact. Create a new layout with fresh visual hierarchy and placement for the headlines and copy.',
  },
  {
    id: 'new_template_new_bg_ref',
    label: 'New template, new image using reference photos',
    prompt:
      'Generate a completely new ad with a fresh layout and background inspired by the reference photos. Create new headline placement and visual hierarchy.',
  },
]

type TextSubMode = 'describe' | 'brand-voice' | 'describe-suggestions'

const TEXT_SUB_MODES: { id: TextSubMode; label: string; description: string }[] = [
  {
    id: 'describe',
    label: 'Describe what you want',
    description: 'Write exactly what changes you want to the ad copy',
  },
  {
    id: 'brand-voice',
    label: 'Brand voice suggestions',
    description: "AI generates copy based on your brand's existing ads",
  },
  {
    id: 'describe-suggestions',
    label: 'Describe + brand voice',
    description: 'Provide direction and let AI blend it with your brand voice',
  },
]

const ASPECT_RATIO_OPTIONS: {
  value: AIAspectRatio
  label: string
  wClass: string
  hClass: string
}[] = [
  { value: '1:1', label: 'Square', wClass: 'w-8', hClass: 'h-8' },
  { value: '16:9', label: 'Landscape', wClass: 'w-10', hClass: 'h-6' },
  { value: '9:16', label: 'Portrait', wClass: 'w-6', hClass: 'h-10' },
]

type UploadedImage = { id: string; name: string; dataUrl: string }

type FocusItem =
  | { kind: 'creative'; creative: AdCreative }
  | { kind: 'upload'; image: UploadedImage }

type Props = {
  businessID: string
  selectedCreatives: AdCreative[]
  uploadedImages: UploadedImage[]
  pickerFiles: PickerFile[]
  onOpenPicker: () => void
  onOpenBusinessFolder: () => void
  onRemovePickerFile: (id: string) => void
  totalSelected: number
  mode: AIGenerationMode
  textSubMode: TextSubMode
  onTextSubModeChange: (subMode: TextSubMode) => void
  // single prompt for text/image modes
  prompt: string
  onPromptChange: (prompt: string) => void
  // separate prompts for both mode
  imagePrompt: string
  textPrompt: string
  onImagePromptChange: (prompt: string) => void
  onTextPromptChange: (prompt: string) => void
  onModeChange: (mode: AIGenerationMode) => void
  aspectRatio: AIAspectRatio
  onAspectRatioChange: (ratio: AIAspectRatio) => void
  onBack: () => void
  onGenerate: () => void
  isSubmitting?: boolean
  hasBusinessFolder?: boolean
}

export function ConfigureStep({
  businessID,
  selectedCreatives,
  uploadedImages,
  pickerFiles,
  onOpenPicker,
  onOpenBusinessFolder,
  onRemovePickerFile,
  totalSelected,
  mode,
  textSubMode,
  onTextSubModeChange,
  prompt,
  onPromptChange,
  imagePrompt,
  textPrompt,
  onImagePromptChange,
  onTextPromptChange,
  onModeChange,
  aspectRatio,
  onAspectRatioChange,
  onBack,
  onGenerate,
  isSubmitting,
  hasBusinessFolder,
}: Props) {
  const [focusedIndex, setFocusedIndex] = useState(0)

  const allItems: FocusItem[] = [
    ...selectedCreatives.map(c => ({ kind: 'creative' as const, creative: c })),
    ...uploadedImages.map(img => ({ kind: 'upload' as const, image: img })),
  ]
  const focused = allItems[focusedIndex] ?? allItems[0]

  const canGenerate =
    mode === 'both'
      ? imagePrompt.trim().length > 0 && textPrompt.trim().length > 0
      : mode === 'image'
      ? imagePrompt.trim().length > 0
      : textSubMode === 'brand-voice'
      ? true
      : prompt.trim().length > 0

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex flex-1 overflow-hidden">
        {/* Left — large preview + thumbnail strip */}
        <div className="flex flex-col w-2/5 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-hidden">
          <div className="flex-1 flex flex-col min-h-0 p-4">
            {focused && (
              <div className="flex-1 flex flex-col min-h-0 rounded-2xl overflow-hidden shadow-lg border border-gray-200 bg-white">
                <div className="flex-1 min-h-0 overflow-hidden">
                  {focused.kind === 'creative' ? (
                    <CreativeMedia
                      creative={focused.creative}
                      className="w-full h-full bg-gray-100"
                      objectFit="contain"
                    />
                  ) : (
                    <img
                      src={focused.image.dataUrl}
                      alt={focused.image.name}
                      className="w-full h-full object-contain bg-gray-100"
                    />
                  )}
                </div>
                <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 flex items-center gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {focused.kind === 'creative' ? focused.creative.name : focused.image.name}
                    </p>
                    <p className="text-xs text-gray-400 capitalize mt-0.5">
                      {focused.kind === 'creative'
                        ? focused.creative.format?.replace('_', ' ')
                        : 'Uploaded photo'}
                    </p>
                  </div>
                  {focused.kind === 'upload' && (
                    <span className="ml-auto flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">
                      Uploaded
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {allItems.length > 1 && (
            <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {allItems.length} selected
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {allItems.map((item, index) => (
                  <button
                    key={item.kind === 'creative' ? item.creative.id : item.image.id}
                    type="button"
                    onClick={() => setFocusedIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      index === focusedIndex
                        ? 'border-purple-600 ring-2 ring-purple-300'
                        : 'border-transparent hover:border-purple-300'
                    }`}
                  >
                    {item.kind === 'creative' ? (
                      <CreativeMedia
                        creative={item.creative}
                        className="w-full h-full bg-gray-200"
                      />
                    ) : (
                      <img
                        src={item.image.dataUrl}
                        alt={item.image.name}
                        className="w-full h-full object-cover bg-gray-200"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right — controls */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-xl mx-auto px-8 py-8 flex flex-col gap-8">
            <section>
              <h2 className="text-base font-semibold text-gray-800 mb-3">
                What should AI generate?
              </h2>
              <GenerationControls mode={mode} onChange={onModeChange} />
            </section>

            {(mode === 'image' || mode === 'both') && (
              <section className="flex flex-col gap-3">
                <label className="text-sm font-semibold text-gray-700">
                  Describe the image you want
                </label>

                {/* Preset buttons */}
                <div className="grid grid-cols-2 gap-2">
                  {IMAGE_PRESETS.map(preset => {
                    const isActive = imagePrompt === preset.prompt
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => onImagePromptChange(isActive ? '' : preset.prompt)}
                        className={`text-left px-3 py-2.5 rounded-lg border text-xs font-medium transition-colors leading-snug ${
                          isActive
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700'
                        }`}
                      >
                        {preset.label}
                      </button>
                    )
                  })}
                </div>

                <textarea
                  value={imagePrompt}
                  onChange={e => onImagePromptChange(e.target.value)}
                  maxLength={IMAGE_LIMIT}
                  rows={3}
                  placeholder="Select a preset above or describe the image changes you want"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div
                  className={`text-xs text-right ${
                    IMAGE_LIMIT - imagePrompt.length < 20 ? 'text-red-500' : 'text-gray-400'
                  }`}
                >
                  {IMAGE_LIMIT - imagePrompt.length} characters remaining
                </div>

                {/* Output format */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-700">Output format</label>
                  <div className="flex gap-2">
                    {ASPECT_RATIO_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => onAspectRatioChange(opt.value)}
                        className={`flex flex-col items-center gap-1.5 px-4 py-2.5 rounded-lg border-2 text-xs font-medium transition-colors ${
                          aspectRatio === opt.value
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50'
                        }`}
                      >
                        <div
                          className={`${opt.wClass} ${opt.hClass} rounded-sm border-2 ${
                            aspectRatio === opt.value
                              ? 'border-purple-600 bg-purple-200'
                              : 'border-gray-400 bg-gray-200'
                          }`}
                        />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Drive reference photos */}
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold text-gray-700">Additional Reference Photos</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={onOpenBusinessFolder}
                      disabled={!hasBusinessFolder}
                      title={
                        !hasBusinessFolder
                          ? 'No business folder configured — set a Google Drive Folder ID in Admin'
                          : undefined
                      }
                      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 hover:border-purple-400 hover:text-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="fab fa-google-drive text-blue-500" />
                      Browse Business Folder
                    </button>
                    <button
                      type="button"
                      onClick={onOpenPicker}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 hover:border-purple-400 hover:text-purple-700 transition-colors"
                    >
                      <i className="fab fa-google-drive text-purple-500" />
                      {pickerFiles.length > 0
                        ? `My Drive (${pickerFiles.length})`
                        : 'Browse my Google Drive'}
                    </button>
                  </div>

                  <PickerSelectedImages files={pickerFiles} onRemove={onRemovePickerFile} />
                </div>
              </section>
            )}

            {mode === 'both' && (
              <section className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700">
                  Describe the copy changes you want
                </label>
                <textarea
                  value={textPrompt}
                  onChange={e => onTextPromptChange(e.target.value)}
                  maxLength={TEXT_LIMIT}
                  rows={4}
                  placeholder='e.g. "Make the headline more urgent and the body copy shorter and punchier"'
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div
                  className={`text-xs text-right ${
                    TEXT_LIMIT - textPrompt.length < 20 ? 'text-red-500' : 'text-gray-400'
                  }`}
                >
                  {TEXT_LIMIT - textPrompt.length} characters remaining
                </div>
                <div className="text-xs text-gray-400 space-y-0.5">
                  <div>
                    Facebook ad copy limits: headline ≤ 40 chars · primary text ≤ 125 chars ·
                    description ≤ 30 chars
                  </div>
                </div>
              </section>
            )}

            {mode === 'text' && (
              <section className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-700">
                    How should AI generate the copy?
                  </label>
                  <div className="flex flex-col gap-2">
                    {TEXT_SUB_MODES.map(sub => {
                      const isActive = textSubMode === sub.id
                      return (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={() => onTextSubModeChange(sub.id)}
                          className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${
                            isActive
                              ? 'border-purple-600 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                          }`}
                        >
                          <p
                            className={`text-sm font-semibold ${
                              isActive ? 'text-purple-700' : 'text-gray-800'
                            }`}
                          >
                            {sub.label}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{sub.description}</p>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {textSubMode !== 'brand-voice' && (
                  <PromptEditor mode={mode} prompt={prompt} onChange={onPromptChange} />
                )}
              </section>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 flex items-center justify-between shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <i className="fa fa-arrow-left" />
          Back
        </button>

        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate || isSubmitting}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-purple-600 text-white font-semibold text-sm hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
        >
          {isSubmitting ? (
            <>
              <i className="fa fa-spinner fa-spin" />
              Preparing...
            </>
          ) : (
            <>
              <i className="fa fa-wand-sparkles" />
              Generate
            </>
          )}
        </button>
      </div>
    </div>
  )
}
